import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Installation } from 'data/api';
import { DetailPanel } from 'src/components/DetailPanel';

type CustomersDetailPanelProps = {
  installations: Installation[];
};

/**
 * CustomersDetailPanel displays customers summary information
 * Shows total customers and health statistics with expandable view of customer data
 */
export const CustomersDetailPanel: FC<CustomersDetailPanelProps> = ({ installations }) => {
  const { projectId } = useParams();

  // Group installations by customer
  const calculateCustomerStats = (allInstallations: Installation[]) => {
    const customerMap = new Map<string, { name: string; installations: Installation[] }>();

    for (const installation of allInstallations) {
      const groupRef = installation.group?.groupRef;
      if (!groupRef) continue;

      if (!customerMap.has(groupRef)) {
        customerMap.set(groupRef, {
          name: installation.group?.groupName || groupRef,
          installations: [],
        });
      }

      customerMap.get(groupRef)!.installations.push(installation);
    }

    return { totalCustomers: customerMap.size, customerMap };
  };

  const { totalCustomers, customerMap } = calculateCustomerStats(installations);

  const badges = [
    { label: 'Total Customers', value: String(totalCustomers) },
  ];

  const description = (
    <>
      Overview of all customers with active installations. Click any customer row below to view
      detailed installation information and configuration.
    </>
  );

  // Create a summary object for the detail view
  const customersOverview = {
    totalCustomers,
    totalInstallations: installations.length,
    customers: Array.from(customerMap.entries()).map(([groupRef, data]) => ({
      groupRef,
      groupName: data.name,
      totalInstallations: data.installations.length,
    })),
  };

  return (
    <DetailPanel
      title="Customers Overview"
      idLabel="Project ID"
      idValue={projectId}
      description={description}
      badges={badges}
      expandButtonText="View customers data"
      detailTitle="Customers Summary"
      fullObject={customersOverview}
    />
  );
};
